/**
 * Utility functions for sanitizing user input to prevent log forging attacks.
 * Removes newlines and control characters that could be used to inject false log entries.
 */

/**
 * Removes newlines, carriage returns, and other control characters from input.
 * This prevents users from injecting fake log entries or breaking log parsers.
 *
 * @param input - The string to sanitize
 * @returns Sanitized string with control characters removed
 */
export const removeControlCharacters = (input: string): string => {
  if (!input) {
    return "";
  }

  return input
    .replace(/\r/g, '')  // Remove carriage return
    .replace(/\n/g, '')  // Remove newline
    .replace(/\t/g, ' ') // Replace tab with space
    .replace(/\0/g, '')  // Remove null character
    .replace(/\x0B/g, '') // Remove vertical tab
    .replace(/\f/g, '');  // Remove form feed
};

/**
 * Sanitizes text input for forms. Can be used in onChange handlers
 * to prevent control characters from being entered.
 *
 * @param event - React change event from input/textarea
 * @returns Sanitized value
 */
export const sanitizeTextInput = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
): string => {
  return removeControlCharacters(event.target.value);
};

/**
 * Sanitizes an entire object's string properties recursively.
 * Useful for sanitizing form data before submission.
 *
 * @param obj - Object to sanitize
 * @returns Sanitized object with all string values cleaned
 */
export const sanitizeObject = <T extends Record<string, any>>(obj: T): T => {
  const sanitized = { ...obj } as any;

  for (const key in sanitized) {
    if (typeof sanitized[key] === 'string') {
      sanitized[key] = removeControlCharacters(sanitized[key] as string);
    } else if (sanitized[key] && typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeObject(sanitized[key]);
    }
  }

  return sanitized as T;
};
